import type { Habilitation, Profil } from '@/api/types';

/**
 * Modules de l'application et profils qui y accèdent (spécifications complémentaires v1.1, § 3.1). Source unique du menu
 * de gauche, du tiroir mobile et de la garde de route : un module absent n'apparaît pas et son adresse renvoie vers
 * « Accès non autorisé ». L'API continue de faire respecter les droits d'action à l'intérieur de chaque module.
 */
export type Module =
  | 'tableau-de-bord'
  | 'tdr'
  | 'fed'
  | 'requisitions'
  | 'achats'
  | 'budget'
  | 'plan'
  | 'parapheur'
  | 'courrier'
  | 'factures'
  | 'instructions'
  | 'delegations'
  | 'tresorerie'
  | 'ressources'
  | 'rapports'
  | 'administration'
  | 'compte'
  | 'rh'
  | 'gestion-rh'
  /** Pseudo-module : droit de voir le calendrier et les congés de son périmètre (directeurs, RH, DGA, DG) ; les pages restent sous « rh ». */
  | 'calendrier'
  /** Pseudo-module : onglets professionnels des prêts sociaux (à traiter, plafonds, portefeuille) ; les pages restent sous « rh ». */
  | 'prets'
  /** Bibliothèque des procédures (démarches RH v1.4, § 6) : ouverte à tout agent ; suivi et gestion selon les droits renvoyés par l'API. */
  | 'procedures';

const TOUS: Profil[] = [
  'EMETTEUR',
  'ASSISTANT',
  'CONSEILLER_TECHNIQUE',
  'DIRECTEUR',
  'CHEF_SERVICE',
  'COORDONNATEUR_MEMORIAL',
  'CONTROLE_GESTION',
  'DIRECTEUR_FINANCIER',
  'DGA_OPERATIONS',
  'DGA_FINANCES',
  'DIRECTEUR_GENERAL',
  'TRESORERIE',
  'TRESORIER',
  'CAISSIER',
  'CHEF_COMPTABLE',
  'CHARGE_ACHATS',
  'RESPONSABLE_ACHATS',
  'CELLULE_MARCHES',
  'DIRECTEUR_RH',
  'RESSOURCES_HUMAINES',
  'INVESTISSEMENTS',
  'PAIE',
  'ADMINISTRATEUR',
];
const sauf = (...exclus: Profil[]): Profil[] => TOUS.filter((p) => !exclus.includes(p));
const FONDS: Profil[] = ['CONTROLE_GESTION', 'DIRECTEUR_FINANCIER', 'DGA_OPERATIONS', 'DGA_FINANCES', 'DIRECTEUR_GENERAL', 'ADMINISTRATEUR'];

export const ACCES_MODULES: Record<Module, Profil[]> = {
  'tableau-de-bord': TOUS,
  tdr: sauf('CAISSIER'),
  fed: TOUS,
  requisitions: sauf('CONSEILLER_TECHNIQUE', 'CAISSIER'),
  achats: [...FONDS, 'DIRECTEUR', 'CHEF_COMPTABLE', 'CHARGE_ACHATS', 'RESPONSABLE_ACHATS', 'CELLULE_MARCHES'],
  budget: sauf('CONSEILLER_TECHNIQUE', 'CAISSIER'),
  plan: ['EMETTEUR', 'ASSISTANT', 'CHEF_SERVICE', 'DIRECTEUR', 'COORDONNATEUR_MEMORIAL', 'CONTROLE_GESTION', 'DIRECTEUR_FINANCIER', 'DGA_OPERATIONS', 'DGA_FINANCES', 'DIRECTEUR_GENERAL', 'ADMINISTRATEUR'],
  parapheur: ['ASSISTANT', 'DIRECTEUR_GENERAL'],
  courrier: [...FONDS, 'ASSISTANT', 'CONSEILLER_TECHNIQUE', 'DIRECTEUR', 'CHEF_COMPTABLE', 'CELLULE_MARCHES'],
  factures: [...FONDS, 'ASSISTANT', 'DIRECTEUR', 'TRESORERIE', 'CHEF_COMPTABLE', 'CHARGE_ACHATS', 'RESPONSABLE_ACHATS', 'CELLULE_MARCHES'],
  instructions: [...FONDS, 'ASSISTANT', 'CONSEILLER_TECHNIQUE', 'DIRECTEUR', 'CHEF_SERVICE', 'TRESORERIE', 'CHEF_COMPTABLE', 'CELLULE_MARCHES'],
  delegations: ['DIRECTEUR_GENERAL', 'ADMINISTRATEUR'],
  tresorerie: ['CONTROLE_GESTION', 'DIRECTEUR_FINANCIER', 'DGA_FINANCES', 'DIRECTEUR_GENERAL', 'TRESORERIE', 'TRESORIER', 'CAISSIER', 'CHEF_COMPTABLE', 'ADMINISTRATEUR'],
  ressources: [...FONDS, 'CHEF_COMPTABLE', 'TRESORERIE'],
  rapports: [...FONDS, 'DIRECTEUR', 'CHEF_SERVICE', 'CHEF_COMPTABLE'],
  administration: [...FONDS, 'CHEF_COMPTABLE', 'DIRECTEUR_RH', 'RESSOURCES_HUMAINES'],
  compte: TOUS,
  // Démarches RH (spécifications v1.4, § 3.1) : ouvert à tout agent actif, même sans profil (voir accedeModule).
  rh: TOUS,
  'gestion-rh': ['DIRECTEUR_RH', 'RESSOURCES_HUMAINES', 'ADMINISTRATEUR', 'DIRECTEUR_GENERAL', 'PAIE', 'DIRECTEUR_FINANCIER'],
  calendrier: ['DIRECTEUR', 'DIRECTEUR_RH', 'RESSOURCES_HUMAINES', 'DGA_OPERATIONS', 'DGA_FINANCES', 'DIRECTEUR_GENERAL', 'ADMINISTRATEUR'],
  prets: ['DIRECTEUR_RH', 'RESSOURCES_HUMAINES', 'INVESTISSEMENTS', 'DIRECTEUR_FINANCIER', 'DGA_FINANCES', 'DIRECTEUR_GENERAL', 'ADMINISTRATEUR', 'CHEF_COMPTABLE', 'TRESORERIE', 'TRESORIER', 'PAIE'],
  procedures: TOUS,
};

/** Préfixes d'adresse de chaque module (la garde de route les compare au chemin courant). */
export const CHEMINS_MODULES: Record<Module, string[]> = {
  'tableau-de-bord': ['/tableau-de-bord'],
  tdr: ['/tdr', '/ne'],
  fed: ['/fed'],
  requisitions: ['/requisitions'],
  achats: ['/achats'],
  budget: ['/budget'],
  plan: ['/plan'],
  parapheur: ['/parapheur'],
  courrier: ['/courrier'],
  factures: ['/factures'],
  instructions: ['/instructions'],
  delegations: ['/delegations'],
  tresorerie: ['/tresorerie'],
  ressources: ['/ressources'],
  rapports: ['/rapports'],
  administration: ['/administration'],
  compte: ['/notifications', '/mot-de-passe'],
  // Avant « rh » : le préfixe le plus précis doit gagner.
  prets: ['/rh/portefeuille', '/rh/prets/retenues', '/rh/prets/recouvrement'],
  'gestion-rh': ['/rh/agents', '/rh/conges/droits', '/rh/non-prestes'],
  rh: ['/rh'],
  calendrier: [],
  procedures: ['/procedures'],
};

/** Modules ouverts à tout agent connecté, même sans habilitation (démarches RH, tableau de bord, compte). */
const MODULES_DE_TOUT_AGENT: ReadonlySet<Module> = new Set<Module>(['tableau-de-bord', 'compte', 'rh', 'procedures']);

export function accedeModule(profils: Profil[], module: Module): boolean {
  if (MODULES_DE_TOUT_AGENT.has(module)) return true;
  return ACCES_MODULES[module].some((p) => profils.includes(p));
}

/** Module dont dépend un chemin ; null pour une adresse hors modules (connexion, aide). */
export function moduleDuChemin(chemin: string): Module | null {
  for (const [module, prefixes] of Object.entries(CHEMINS_MODULES) as Array<[Module, string[]]>) {
    if (prefixes.some((p) => chemin === p || chemin.startsWith(`${p}/`))) return module;
  }
  return null;
}

/** Profils qui voient tout le Fonds (habilitation sans structure). */
export const voitToutLeFonds = (habilitations: Habilitation[]) => habilitations.some((h) => h.structureId === null);

/**
 * Droit d'émission des TDR, FED et réquisitions (v1.1, § 3.2) : émetteur, assistant ou directeur sur leur périmètre ;
 * responsable de la trésorerie et trésorier pour leur propre structure. Le DG, les DGA et le DF n'émettent pas.
 */
export const peutEmettreDocuments = (habilitations: Habilitation[]) =>
  habilitations.some((h) => h.profil === 'EMETTEUR' || h.profil === 'ASSISTANT' || h.profil === 'DIRECTEUR' || h.profil === 'TRESORERIE' || h.profil === 'TRESORIER');

/** Émission d'un FED depuis un bon de commande ou une facture (achats et Cellule des marchés). */
export const peutEmettreFedAchats = (habilitations: Habilitation[]) => habilitations.some((h) => h.profil === 'CELLULE_MARCHES' || h.profil === 'CHARGE_ACHATS' || h.profil === 'RESPONSABLE_ACHATS');
